import type { Timestamp } from '../types';

/**
 * The achievement catalogue, and the pure function that decides what a player
 * has earned.
 *
 * It lives in `shared` for the same reason the board rules do: the server
 * evaluates it to decide what to unlock, and the client renders the very same
 * list, progress bars for locked entries included. Two copies of this table
 * would drift within a release.
 *
 * Nothing here reads a clock, a database or a socket. `evaluate` takes a
 * snapshot of counters and returns what they add up to.
 */

export type AchievementCategory = 'combate' | 'score' | 'social';

/** Ordered from most common to rarest; the UI colours by this. */
export type AchievementRarity = 'comun' | 'raro' | 'epico' | 'legendario';

export const ACHIEVEMENT_CATEGORIES: readonly AchievementCategory[] = [
  'combate',
  'score',
  'social',
];

/**
 * Cumulative counters for one player. Maintained by the server in the same
 * transaction that closes a match, so they are always exactly as old as the
 * last match that finished.
 */
export interface PlayerProgress {
  matchesPlayed: number;
  versusPlayed: number;
  versusWins: number;
  /** Wins where the opponent's HP actually reached zero, not a timeout or a quit. */
  koWins: number;
  /** Wins taken without losing a single point of HP. */
  flawlessWins: number;
  /** Wins against someone rated well above you. Feeds the secret achievement. */
  upsetWins: number;
  soloPlayed: number;
  totalScore: number;
  bestScore: number;
  maxTile: number;
  maxCombo: number;
  totalDamage: number;
  bestWinStreak: number;
  distinctOpponents: number;
  /** Matches played against the single opponent faced most often. */
  topRivalMatches: number;
}

export const EMPTY_PROGRESS: PlayerProgress = {
  matchesPlayed: 0,
  versusPlayed: 0,
  versusWins: 0,
  koWins: 0,
  flawlessWins: 0,
  upsetWins: 0,
  soloPlayed: 0,
  totalScore: 0,
  bestScore: 0,
  maxTile: 0,
  maxCombo: 0,
  totalDamage: 0,
  bestWinStreak: 0,
  distinctOpponents: 0,
  topRivalMatches: 0,
};

export interface AchievementDefinition {
  readonly id: string;
  /** Shown uppercase by the UI; stored as written. */
  readonly name: string;
  readonly description: string;
  readonly category: AchievementCategory;
  readonly rarity: AchievementRarity;
  /** The counter value that unlocks it. */
  readonly goal: number;
  /** Sprite symbol id, without the `icon-` prefix. */
  readonly icon: string;
  /**
   * Hidden until earned: the list shows a placeholder instead of the name and
   * the goal. Kept out of the "how do I get this" flow on purpose.
   */
  readonly secret?: boolean;
  /** Which counter this achievement watches. */
  readonly measure: (progress: PlayerProgress) => number;
}

/**
 * Rarity is a statement about how hard something is, not about how rare it
 * happens to be today, so it is authored rather than computed from unlocks.
 */
export const ACHIEVEMENTS: readonly AchievementDefinition[] = [
  // --- combate ---------------------------------------------------------------
  {
    id: 'primera-sangre',
    name: 'PRIMERA SANGRE',
    description: 'GANA TU PRIMERA PARTIDA VERSUS',
    category: 'combate',
    rarity: 'comun',
    goal: 1,
    icon: 'swords',
    measure: (p) => p.versusWins,
  },
  {
    id: 'ko-tecnico',
    name: 'K.O. TÉCNICO',
    description: 'GANA 10 PARTIDAS POR K.O.',
    category: 'combate',
    rarity: 'comun',
    goal: 10,
    icon: 'bolt',
    measure: (p) => p.koWins,
  },
  {
    id: 'veterano',
    name: 'VETERANO',
    description: 'JUEGA 100 PARTIDAS',
    category: 'combate',
    rarity: 'comun',
    goal: 100,
    icon: 'timer',
    measure: (p) => p.matchesPlayed,
  },
  {
    id: 'verdugo',
    name: 'VERDUGO',
    description: 'GANA 50 PARTIDAS VERSUS',
    category: 'combate',
    rarity: 'raro',
    goal: 50,
    icon: 'swords',
    measure: (p) => p.versusWins,
  },
  {
    id: 'imparable',
    name: 'IMPARABLE',
    description: 'ENCADENA 5 VICTORIAS SEGUIDAS',
    category: 'combate',
    rarity: 'raro',
    goal: 5,
    icon: 'bolt',
    measure: (p) => p.bestWinStreak,
  },
  {
    id: 'artillero',
    name: 'ARTILLERO',
    description: 'INFLIGE 100 000 DE DAÑO',
    category: 'combate',
    rarity: 'raro',
    goal: 100_000,
    icon: 'bolt',
    measure: (p) => p.totalDamage,
  },
  {
    id: 'sin-un-rasguno',
    name: 'SIN UN RASGUÑO',
    description: 'GANA UNA PARTIDA SIN RECIBIR DAÑO',
    category: 'combate',
    rarity: 'epico',
    goal: 1,
    icon: 'trophy',
    measure: (p) => p.flawlessWins,
  },
  {
    id: 'dominio-total',
    name: 'DOMINIO TOTAL',
    description: 'ENCADENA 10 VICTORIAS SEGUIDAS',
    category: 'combate',
    rarity: 'epico',
    goal: 10,
    icon: 'crown',
    measure: (p) => p.bestWinStreak,
  },
  {
    id: 'leyenda-arena',
    name: 'LEYENDA DE LA ARENA',
    description: 'GANA 250 PARTIDAS VERSUS',
    category: 'combate',
    rarity: 'legendario',
    goal: 250,
    icon: 'crown',
    measure: (p) => p.versusWins,
  },

  // --- score -----------------------------------------------------------------
  {
    id: 'aprendiz',
    name: 'APRENDIZ',
    description: 'CONSIGUE UNA FICHA 512',
    category: 'score',
    rarity: 'comun',
    goal: 512,
    icon: 'play',
    measure: (p) => p.maxTile,
  },
  {
    id: 'maraton',
    name: 'MARATÓN',
    description: 'JUEGA 50 PARTIDAS EN SOLITARIO',
    category: 'score',
    rarity: 'comun',
    goal: 50,
    icon: 'infinity',
    measure: (p) => p.soloPlayed,
  },
  {
    id: 'maestro',
    name: 'MAESTRO',
    description: 'CONSIGUE UNA FICHA 1024',
    category: 'score',
    rarity: 'raro',
    goal: 1024,
    icon: 'medal-silver',
    measure: (p) => p.maxTile,
  },
  {
    id: 'cadena-mortal',
    name: 'CADENA MORTAL',
    description: 'LLEGA A UN COMBO x15',
    category: 'score',
    rarity: 'raro',
    goal: 15,
    icon: 'bolt',
    measure: (p) => p.maxCombo,
  },
  {
    id: 'seis-cifras',
    name: 'SEIS CIFRAS',
    description: 'ALCANZA 100 000 PUNTOS EN UNA PARTIDA',
    category: 'score',
    rarity: 'raro',
    goal: 100_000,
    icon: 'trophy',
    measure: (p) => p.bestScore,
  },
  {
    id: 'primer-2048',
    name: 'PRIMER 2048',
    description: 'CONSIGUE TU PRIMERA FICHA 2048',
    category: 'score',
    rarity: 'epico',
    goal: 2048,
    icon: 'trophy',
    measure: (p) => p.maxTile,
  },
  {
    id: 'reaccion-en-cadena',
    name: 'REACCIÓN EN CADENA',
    description: 'LLEGA A UN COMBO x25',
    category: 'score',
    rarity: 'epico',
    goal: 25,
    icon: 'bolt',
    measure: (p) => p.maxCombo,
  },
  {
    id: 'medio-millon',
    name: 'MEDIO MILLÓN',
    description: 'ACUMULA 500 000 PUNTOS',
    category: 'score',
    rarity: 'legendario',
    goal: 500_000,
    icon: 'crown',
    measure: (p) => p.totalScore,
  },
  {
    id: 'mas-alla',
    name: 'MÁS ALLÁ',
    description: 'CONSIGUE UNA FICHA 4096',
    category: 'score',
    rarity: 'legendario',
    goal: 4096,
    icon: 'crown',
    measure: (p) => p.maxTile,
  },

  // --- social ----------------------------------------------------------------
  {
    id: 'primer-rival',
    name: 'PRIMER RIVAL',
    description: 'ENFRÉNTATE A OTRO JUGADOR',
    category: 'social',
    rarity: 'comun',
    goal: 1,
    icon: 'bot',
    measure: (p) => p.distinctOpponents,
  },
  {
    id: 'sociable',
    name: 'SOCIABLE',
    description: 'ENFRÉNTATE A 10 RIVALES DISTINTOS',
    category: 'social',
    rarity: 'comun',
    goal: 10,
    icon: 'bot',
    measure: (p) => p.distinctOpponents,
  },
  {
    id: 'rival-fiel',
    name: 'RIVAL FIEL',
    description: 'JUEGA 10 PARTIDAS CONTRA EL MISMO RIVAL',
    category: 'social',
    rarity: 'raro',
    goal: 10,
    icon: 'swords',
    measure: (p) => p.topRivalMatches,
  },
  {
    id: 'cara-conocida',
    name: 'CARA CONOCIDA',
    description: 'ENFRÉNTATE A 50 RIVALES DISTINTOS',
    category: 'social',
    rarity: 'epico',
    goal: 50,
    icon: 'medal-silver',
    measure: (p) => p.distinctOpponents,
  },

  // --- secret ----------------------------------------------------------------
  {
    id: 'david-contra-goliat',
    name: 'DAVID CONTRA GOLIAT',
    description: 'GANA A UN RIVAL 300 PUNTOS DE ELO POR ENCIMA',
    category: 'combate',
    rarity: 'epico',
    goal: 1,
    icon: 'trophy',
    secret: true,
    measure: (p) => p.upsetWins,
  },
];

export const ACHIEVEMENT_TOTAL = ACHIEVEMENTS.length;

const BY_ID = new Map(ACHIEVEMENTS.map((achievement) => [achievement.id, achievement]));

export function achievementById(id: string): AchievementDefinition | undefined {
  return BY_ID.get(id);
}

/** One achievement as the API returns it and the screen draws it. */
export interface AchievementState {
  id: string;
  /** Counter value, capped at the goal so a bar never overfills. */
  progress: number;
  goal: number;
  unlockedAt: Timestamp | null;
}

/**
 * What this snapshot of counters adds up to, across the whole catalogue.
 *
 * Deliberately total rather than incremental: an achievement added to the
 * catalogue after a player already earned the underlying progress unlocks on
 * their next match, instead of being unreachable because the moment passed.
 */
export function evaluate(
  progress: PlayerProgress,
  unlockedAt: ReadonlyMap<string, Timestamp>,
  at: Timestamp,
): AchievementState[] {
  return ACHIEVEMENTS.map((achievement) => {
    const value = Math.min(achievement.measure(progress), achievement.goal);
    const already = unlockedAt.get(achievement.id) ?? null;
    return {
      id: achievement.id,
      progress: value,
      goal: achievement.goal,
      unlockedAt: already ?? (value >= achievement.goal ? at : null),
    };
  });
}

/** The ids `evaluate` just turned from locked to unlocked. */
export function newlyUnlocked(
  states: readonly AchievementState[],
  previous: ReadonlyMap<string, Timestamp>,
): string[] {
  return states
    .filter((state) => state.unlockedAt !== null && !previous.has(state.id))
    .map((state) => state.id);
}
